import React, { createContext, useContext, useState, useCallback } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
const COLORS = {
  success: '#2DAD51',
  error:   '#E53935',
  warning: '#F57F17',
};

const DURATION = 2000;

// ─── Context ─────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

const showToast = useCallback((type, title, message) => {
    const msg = message !== undefined ? message : title;
    
    setToasts(prev => {
      if (prev.some(t => t.message === msg)) return prev;
      const id = Date.now() + Math.random();
      setTimeout(() => dismissToast(id), DURATION);
      return [...prev, { id, type, message: msg }];
    });
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

// ─── Container ───────────────────────────────────────────────────────────────
function ToastContainer({ toasts, onDismiss }) {
  return (
    <div style={{
      position: 'fixed',
      top: 20,
      right: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      zIndex: 9999,
      alignItems: 'flex-end',
      pointerEvents: 'none',
    }}>
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  );
}

// ─── Single Toast ─────────────────────────────────────────────────────────────
function Toast({ type, message }) {
  const color = COLORS[type] || COLORS.success;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12, 
          background: '#FFFFFF',
          border: '1px solid #EBEBF0',
          borderRadius: 999, 
          padding: '12px 18px 12px 12px', 
          boxShadow: '0 3px 10px rgba(0,0,0,0.12)', 
          animation: 'slideInRight 0.5s cubic-bezier(0.22,1,0.36,1) forwards',
          pointerEvents: 'all',
          minWidth: 220,
          maxWidth: 320,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
            flexShrink: 0,
          }}
        />

        {/* TEXT */}
        <span
          style={{
            fontSize: 14, 
            fontWeight: 600,
            color: '#1A1A2E',
            lineHeight: 1.5,
            wordBreak: 'break-word', 
          }}
        >
          {message}
        </span>
      </div>
    </>
  );
}